import semverCoerce from "semver/functions/coerce";
import semverParse from "semver/functions/parse";

import type { Instance } from "@/schemas";

/**
 * Truth Social, the Mastodon fork powering truthsocial.com
 * @see {@link https://help.truthsocial.com/legal/open-source}
 */
export const TRUTHSOCIAL = "TruthSocial";

/** Backend name reserved only for tests. */
export const UNRELEASED = "unreleased";

/** Parse features for the given instance */
export const getFeatures = (instance?: Instance) => {
  const v = instance
    ? parseVersion(instance.version)
    : {
        build: null,
        compatVersion: "0.0.0",
        software: TRUTHSOCIAL,
        version: "0.0.0",
      };

  return {
    /**
     * Ability to only chat with people that follow you.
     */
    bookmarks: true,

    /**
     * Save drafts
     */
    drafts: true,

    groupVotes: true,

    /**
     * Can use premium features
     */
    premium: true,

    /**
     * Pinned announcements
     */
    announcements: true,

    /**
     * Predictions
     */
    predictions: true,

    /**
     * Can view Sports features
     */
    sports: true,

    /**
     * Translations
     */
    translations: v.build === UNRELEASED,

    /**
     * HTML ads rendered in a sandboxed iframe
     */
    htmlAds: v.build === UNRELEASED,

    /**
     * Paid post boosts (composer toggle, confirmation modal, dashboard).
     * Gated per-account by the server's `boosts` feature flag in
     * useFeatures; the backend additionally enforces availability via
     * the /api/v1/boosts/eligibility endpoint.
     */
    boosts: true,

    impressions: true,
  };
};

/** Features available from a backend */
export type Features = ReturnType<typeof getFeatures>;

/** Fediverse backend */
interface Backend {
  /** Build name, if this software is a fork */
  build: string | null;
  /** Name of the software */
  software: string | null;
  /** API version number */
  version: string;
  /** Mastodon API version this backend is compatible with */
  compatVersion: string;
}

/** Get information about the software from its version string */
export const parseVersion = (version: string): Backend => {
  const regex = /^([\w+.]*)(?: \(compatible; ([\w]*) (.*)\))?$/;
  const match = regex.exec(version);

  const semverString = match && (match[3] || match[1]);
  const semver = match
    ? semverParse(semverString) ||
      semverCoerce(semverString, {
        loose: true,
      })
    : null;
  const compat = match ? semverParse(match[1]) || semverCoerce(match[1]) : null;

  if (match && semver && compat) {
    return {
      build: semver.build[0],
      compatVersion: compat.version,
      software: match[2] || TRUTHSOCIAL,
      version: semver.version,
    };
  } else {
    // If we can't parse the version, this is a new and exotic backend.
    // Fall back to minimal featureset.
    return {
      build: null,
      compatVersion: "0.0.0",
      software: null,
      version: "0.0.0",
    };
  }
};
